import { useCallback } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
} from 'src/components/CustomButton';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { PROMPT_ACTION } from 'src/data/prompt';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  variables: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onAction: PropTypes.func.isRequired,
};

const defaultProps = {
  className: '',
};

export const Variables = ({ className, onAction, variables }) => {
  const styles = useStyles();

  const handleVariableClick = useCallback(
    (variable) => {
      switch (variable.resourceType) {
        case RESOURCE_CONTENT_TYPE.url:
          // "long form" view puts the full URL in the "title" ("text" contains truncated value), so we use that if available
          onAction(PROMPT_ACTION.insertUrl, { url: variable.title || variable.text, noTags: true });
          break;

        case RESOURCE_CONTENT_TYPE.image:
        case RESOURCE_CONTENT_TYPE.document:
        case RESOURCE_CONTENT_TYPE.audio:
        case RESOURCE_CONTENT_TYPE.video:
          onAction(PROMPT_ACTION.insertFileResource, {
            resource: {
              id: variable.id,
              contentType: variable.resourceType,
              name: variable.text,
            },
          });
          break;

        case RESOURCE_CONTENT_TYPE.messagingBankAsset:
          onAction(PROMPT_ACTION.insertBrandBrief, { item: variable });
          break;

        default:
          onAction(PROMPT_ACTION.insertVariable, { variable });
      }
    },
    [onAction]
  );

  return (
    <div className={cx(styles.root, className)}>
      <CustomTypography
        className={styles.title}
        variant={TYPOGRAPHY_VARIANT.regular12}
      >
        Use in this section:
      </CustomTypography>

      <div className={styles.variables}>
        {variables.map((variable) => (
          <CustomTooltip
            classes={{ tooltip: styles.tooltip }}
            key={variable.title}
            title={variable.infoTooltip}
          >
            <CustomButton
              className={styles.button}
              variant={CUSTOM_BUTTON_VARIANT.outlined}
              size={CUSTOM_BUTTON_SIZE.smaller}
              onClick={() => handleVariableClick(variable)}
            >
              {variable?.text}
            </CustomButton>
          </CustomTooltip>
        ))}
      </div>
    </div>
  );
};

Variables.propTypes = propTypes;
Variables.defaultProps = defaultProps;
